/**
 * ChatPanel — The main chat panel UI anchored to the floating action button.
 *
 * Contains a header with title + controls, a scrollable message list,
 * and an input area at the bottom. Sized ~400x600px on desktop, full-screen
 * on mobile (< 640px).
 */

import { useState, useRef, useEffect } from 'react';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';

/**
 * @param {{
 *   messages: Array,
 *   isLoading: boolean,
 *   onSend: (text: string) => void,
 *   onReset: () => void,
 *   onClose: () => void
 * }} props
 */
export default function ChatPanel({ messages, isLoading, onSend, onReset, onClose }) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to the bottom when new messages arrive or loading state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus the input when the panel mounts
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /**
   * Handle form submission — send the message and clear the input.
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    onSend(input);
    setInput('');
  };

  /**
   * Handle keyboard shortcuts — Enter to send, Shift+Enter for newline.
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div
      className="
        fixed bottom-20 right-4 sm:right-6
        w-[calc(100vw-2rem)] sm:w-[400px]
        h-[calc(100vh-6rem)] sm:h-[600px]
        bg-white rounded-2xl shadow-2xl border border-gray-200
        flex flex-col overflow-hidden z-50
        animate-chat-panel-in
      "
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-900">OMS Assistant</h3>
        <div className="flex items-center space-x-1">
          {/* Reset button */}
          <button
            onClick={onReset}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Reset conversation"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {/* Close button */}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close chat"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
        {messages.length === 0 && !isLoading && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm text-center px-4">
            <p>Send a message to start chatting with the OMS Assistant.</p>
          </div>
        )}

        {/* Render each message */}
        {messages.map((msg, i) => (
          <ChatMessage key={i} message={msg} />
        ))}

        {/* Typing indicator while waiting for agent response */}
        {isLoading && <TypingIndicator />}

        {/* Invisible anchor for auto-scrolling */}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <form
        onSubmit={handleSubmit}
        className="flex items-end gap-2 px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0"
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about purchase orders, suppliers…"
          rows={1}
          className="
            flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
            max-h-24 overflow-y-auto
          "
          disabled={isLoading}
        />
        <button
          type="submit"
          disabled={!input.trim() || isLoading}
          className="
            p-2.5 rounded-xl bg-blue-600 text-white
            hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed
            transition-colors flex-shrink-0
          "
          title="Send message"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </form>
    </div>
  );
}
